/**
 * Data Decimation Utilities
 *
 * Reduces sensor data density at distant (low) zoom levels to improve
 * performance without significantly impacting visual quality.
 *
 * Phase 8.1.2: Data decimation for distant zoom levels
 */

import type { SensorDataPoint } from '../MapComponent.types';

// ============================================================================
// Types
// ============================================================================

export interface DecimationOptions {
  /**
   * Decimation strategy to use
   * - 'none': No decimation
   * - 'grid': Grid-based spatial sampling
   * - 'random': Random sampling with consistent seed
   * - 'priority': Priority-based (extreme values first)
   * @default 'grid'
   */
  strategy?: 'none' | 'grid' | 'random' | 'priority';
  /**
   * Target number of sensors at lowest zoom level
   * Actual count will be interpolated between this and full dataset
   * @default 100
   */
  minSensors?: number;
  /**
   * Zoom level below which decimation starts
   * @default 8
   */
  decimationStartZoom?: number;
  /**
   * Zoom level above which no decimation occurs
   * @default 14
   */
  decimationEndZoom?: number;
  /**
   * Grid cell size in degrees for grid-based decimation
   * Smaller = more detail, larger = more aggressive decimation
   * @default auto-calculated based on zoom
   */
  gridCellSize?: number;
}

export interface DecimationResult {
  /** Decimated sensor data */
  sensors: SensorDataPoint[];
  /** Original sensor count */
  originalCount: number;
  /** Decimation ratio (0.0 - 1.0) */
  ratio: number;
  /** Strategy used */
  strategy: string;
  /** Processing time in ms */
  processingTime: number;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MIN_SENSORS = 100;
const DEFAULT_DECIMATION_START_ZOOM = 8;
const DEFAULT_DECIMATION_END_ZOOM = 14;

// Grid cell sizes (in degrees) for different zoom levels
const ZOOM_GRID_SIZES: Record<number, number> = {
  0: 10,    // ~1100km cells
  2: 5,     // ~550km cells
  4: 2,     // ~220km cells
  6: 0.5,   // ~55km cells
  8: 0.1,   // ~11km cells
  10: 0.02, // ~2.2km cells
  12: 0.005, // ~550m cells
};

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Calculate decimation ratio based on zoom level
 *
 * Returns a ratio between 0 and 1 indicating what fraction of
 * sensors to display. Higher zoom = higher ratio (more detail).
 *
 * @param zoom - Current zoom level (0-22)
 * @param startZoom - Zoom where decimation starts
 * @param endZoom - Zoom where decimation ends (full detail)
 * @returns Decimation ratio (0.0 - 1.0)
 */
export function calculateDecimationRatio(
  zoom: number,
  startZoom: number = DEFAULT_DECIMATION_START_ZOOM,
  endZoom: number = DEFAULT_DECIMATION_END_ZOOM
): number {
  if (zoom >= endZoom) {
    return 1.0; // Full detail
  }

  if (zoom <= startZoom) {
    return 0.0; // Maximum decimation
  }

  // Linear interpolation
  return (zoom - startZoom) / (endZoom - startZoom);
}

/**
 * Calculate grid cell size based on zoom level
 *
 * @param zoom - Current zoom level
 * @returns Cell size in degrees
 */
export function calculateGridCellSize(zoom: number): number {
  // Find the two closest zoom levels and interpolate
  const zoomLevels = Object.keys(ZOOM_GRID_SIZES)
    .map(Number)
    .sort((a, b) => a - b);

  let lowerZoom = zoomLevels[0];
  let upperZoom = zoomLevels[zoomLevels.length - 1];

  for (let i = 0; i < zoomLevels.length - 1; i++) {
    if (zoom >= zoomLevels[i] && zoom < zoomLevels[i + 1]) {
      lowerZoom = zoomLevels[i];
      upperZoom = zoomLevels[i + 1];
      break;
    }
  }

  if (zoom >= upperZoom) {
    return ZOOM_GRID_SIZES[upperZoom];
  }

  if (zoom <= lowerZoom) {
    return ZOOM_GRID_SIZES[lowerZoom];
  }

  // Exponential interpolation for smoother transition
  const t = (zoom - lowerZoom) / (upperZoom - lowerZoom);
  const lowerSize = ZOOM_GRID_SIZES[lowerZoom];
  const upperSize = ZOOM_GRID_SIZES[upperZoom];

  return lowerSize * Math.pow(upperSize / lowerSize, t);
}

/**
 * Grid-based decimation: Select one sensor per grid cell
 *
 * Divides the map into a grid and selects a representative sensor
 * from each cell. Provides even spatial distribution.
 *
 * @param sensors - Full sensor array
 * @param gridCellSize - Grid cell size in degrees
 * @param selectBestInCell - Function to select best sensor from cell
 * @returns Decimated sensors
 */
export function decimateByGrid(
  sensors: SensorDataPoint[],
  gridCellSize: number,
  selectBestInCell: (cellSensors: SensorDataPoint[]) => SensorDataPoint = selectCellRepresentative
): SensorDataPoint[] {
  const startTime = performance.now();

  if (sensors.length === 0) return [];

  // Group sensors into grid cells
  const grid = new Map<string, SensorDataPoint[]>();

  for (const sensor of sensors) {
    const cellX = Math.floor(sensor.longitude / gridCellSize);
    const cellY = Math.floor(sensor.latitude / gridCellSize);
    const key = `${cellX},${cellY}`;

    if (!grid.has(key)) {
      grid.set(key, []);
    }
    grid.get(key)!.push(sensor);
  }

  // Select representative sensor from each cell
  const decimated: SensorDataPoint[] = [];
  for (const cellSensors of grid.values()) {
    decimated.push(selectBestInCell(cellSensors));
  }

  console.log(
    `[DataDecimation] Grid decimation: ${sensors.length} -> ${decimated.length} sensors ` +
    `(${grid.size} cells, ${gridCellSize.toFixed(4)}° cells) ` +
    `in ${(performance.now() - startTime).toFixed(2)}ms`
  );

  return decimated;
}

/**
 * Select representative sensor from a grid cell
 *
 * Uses a weighted selection based on:
 * 1. Extreme moisture values (most interesting)
 * 2. Most recent timestamp (freshest data)
 * 3. Central position (visual balance)
 *
 * @param cellSensors - Sensors within a grid cell
 * @returns Representative sensor
 */
export function selectCellRepresentative(cellSensors: SensorDataPoint[]): SensorDataPoint {
  if (cellSensors.length === 1) {
    return cellSensors[0];
  }

  // Score each sensor
  const scores = cellSensors.map((sensor) => {
    let score = 0;

    // Prioritize extreme moisture values (interesting for visualization)
    const moistureExtremity = Math.abs(sensor.moistureLevel - 50);
    score += moistureExtremity / 50; // 0-1 based on how extreme

    // Prioritize recent updates
    const timestamp = typeof sensor.timestamp === 'string'
      ? new Date(sensor.timestamp).getTime()
      : sensor.timestamp?.getTime?.() || 0;
    const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);
    score += Math.max(0, 1 - ageHours / 24); // 0-1 based on recency (24h window)

    return { sensor, score };
  });

  // Return highest scoring sensor
  scores.sort((a, b) => b.score - a.score);
  return scores[0].sensor;
}

/**
 * Priority-based decimation: Keep most important sensors
 *
 * Prioritizes sensors with extreme values or recent updates.
 * Good for anomaly detection where you want to see problems.
 *
 * @param sensors - Full sensor array
 * @param targetCount - Target number of sensors to keep
 * @returns Decimated sensors
 */
export function decimateByPriority(
  sensors: SensorDataPoint[],
  targetCount: number
): SensorDataPoint[] {
  const startTime = performance.now();

  if (sensors.length <= targetCount) return sensors;

  // Score sensors by importance
  const scored = sensors.map((sensor) => {
    let priority = 0;

    // Extreme moisture values (drought or flooding)
    if (sensor.moistureLevel < 20 || sensor.moistureLevel > 80) {
      priority += 3;
    } else if (sensor.moistureLevel < 35 || sensor.moistureLevel > 65) {
      priority += 1;
    }

    // Temperature anomalies (if present)
    if (sensor.temperature !== undefined) {
      if (sensor.temperature < 5 || sensor.temperature > 35) {
        priority += 2;
      }
    }

    // Recent data is more important
    const timestamp = typeof sensor.timestamp === 'string'
      ? new Date(sensor.timestamp).getTime()
      : sensor.timestamp?.getTime?.() || 0;
    const ageMinutes = (Date.now() - timestamp) / (1000 * 60);
    if (ageMinutes < 30) {
      priority += 2;
    } else if (ageMinutes < 120) {
      priority += 1;
    }

    return { sensor, priority };
  });

  // Sort by priority and take top N
  scored.sort((a, b) => b.priority - a.priority);
  const decimated = scored.slice(0, targetCount).map((s) => s.sensor);

  console.log(
    `[DataDecimation] Priority decimation: ${sensors.length} -> ${decimated.length} sensors ` +
    `in ${(performance.now() - startTime).toFixed(2)}ms`
  );

  return decimated;
}

/**
 * Random decimation with consistent seeding
 *
 * Uses a simple hash-based selection to ensure consistent
 * results across renders (same sensors always selected).
 *
 * @param sensors - Full sensor array
 * @param ratio - Fraction to keep (0.0 - 1.0)
 * @param seed - Seed for consistent selection
 * @returns Decimated sensors
 */
export function decimateByRandom(
  sensors: SensorDataPoint[],
  ratio: number,
  seed: number = 12345
): SensorDataPoint[] {
  const startTime = performance.now();

  if (ratio >= 1) return sensors;
  if (ratio <= 0) return [];

  const targetCount = Math.max(1, Math.floor(sensors.length * ratio));

  // Simple deterministic selection based on sensor ID hash
  const decimated = sensors
    .map((sensor) => ({
      sensor,
      hash: simpleHash(sensor.id + seed.toString()),
    }))
    .sort((a, b) => a.hash - b.hash)
    .slice(0, targetCount)
    .map((s) => s.sensor);

  console.log(
    `[DataDecimation] Random decimation: ${sensors.length} -> ${decimated.length} sensors ` +
    `(ratio: ${(ratio * 100).toFixed(1)}%) ` +
    `in ${(performance.now() - startTime).toFixed(2)}ms`
  );

  return decimated;
}

/**
 * Simple hash function for consistent random selection
 */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Main decimation function: Apply zoom-based decimation
 *
 * Combines all decimation strategies with automatic zoom-based
 * parameter selection.
 *
 * @param sensors - Full sensor array
 * @param zoom - Current map zoom level
 * @param options - Decimation options
 * @returns Decimation result with stats
 *
 * @example
 * ```typescript
 * const result = decimateSensors(sensorData, viewState.zoom, {
 *   strategy: 'grid',
 *   minSensors: 100,
 * });
 * console.log(`Showing ${result.sensors.length} sensors (${result.ratio * 100}%)`);
 * ```
 */
export function decimateSensors(
  sensors: SensorDataPoint[],
  zoom: number,
  options: DecimationOptions = {}
): DecimationResult {
  const startTime = performance.now();

  const {
    strategy = 'grid',
    minSensors = DEFAULT_MIN_SENSORS,
    decimationStartZoom = DEFAULT_DECIMATION_START_ZOOM,
    decimationEndZoom = DEFAULT_DECIMATION_END_ZOOM,
    gridCellSize: customGridCellSize,
  } = options;

  // Calculate decimation ratio for current zoom
  const ratio = calculateDecimationRatio(zoom, decimationStartZoom, decimationEndZoom);

  // No decimation needed at high zoom
  if (ratio >= 1.0 || strategy === 'none') {
    return {
      sensors,
      originalCount: sensors.length,
      ratio: 1.0,
      strategy: 'none',
      processingTime: performance.now() - startTime,
    };
  }

  // Calculate target sensor count
  const targetCount = Math.max(
    minSensors,
    Math.floor(sensors.length * ratio)
  );

  // Apply decimation strategy
  let decimated: SensorDataPoint[];

  switch (strategy) {
    case 'grid': {
      const gridCellSize = customGridCellSize || calculateGridCellSize(zoom);
      decimated = decimateByGrid(sensors, gridCellSize);
      // If grid produces too many, apply random selection
      if (decimated.length > targetCount) {
        decimated = decimateByRandom(decimated, targetCount / decimated.length);
      }
      break;
    }

    case 'priority':
      decimated = decimateByPriority(sensors, targetCount);
      break;

    case 'random':
      decimated = decimateByRandom(sensors, ratio);
      break;

    default:
      decimated = sensors;
  }

  // Ensure minimum sensor count
  if (decimated.length < minSensors && sensors.length >= minSensors) {
    console.log(`[DataDecimation] Below minimum, expanding to ${minSensors} sensors`);
    decimated = decimateByPriority(sensors, minSensors);
  }

  const processingTime = performance.now() - startTime;

  console.log(
    `[DataDecimation] ${strategy} at zoom ${zoom.toFixed(1)}: ` +
    `${sensors.length} -> ${decimated.length} sensors ` +
    `(target ratio: ${(ratio * 100).toFixed(1)}%) ` +
    `in ${processingTime.toFixed(2)}ms`
  );

  return {
    sensors: decimated,
    originalCount: sensors.length,
    ratio: sensors.length > 0 ? decimated.length / sensors.length : 1,
    strategy,
    processingTime,
  };
}

/**
 * Calculate recommended decimation settings based on dataset size
 *
 * @param sensorCount - Total number of sensors
 * @returns Recommended decimation options
 */
export function getRecommendedDecimationSettings(
  sensorCount: number
): DecimationOptions {
  if (sensorCount <= 100) {
    return { strategy: 'none' };
  }

  if (sensorCount <= 500) {
    return {
      strategy: 'grid',
      minSensors: Math.floor(sensorCount * 0.2),
      decimationStartZoom: 6,
      decimationEndZoom: 12,
    };
  }

  if (sensorCount <= 1000) {
    return {
      strategy: 'grid',
      minSensors: 100,
      decimationStartZoom: 8,
      decimationEndZoom: 14,
    };
  }

  // Large datasets (1000+)
  return {
    strategy: 'grid',
    minSensors: 150,
    decimationStartZoom: 10,
    decimationEndZoom: 16,
  };
}

export default {
  calculateDecimationRatio,
  calculateGridCellSize,
  decimateByGrid,
  selectCellRepresentative,
  decimateByPriority,
  decimateByRandom,
  decimateSensors,
  getRecommendedDecimationSettings,
};
